import * as dateFns from 'date-fns';

/*
 * Version 1 of the 'date-fns' library has no way of natively displaying time in specified
 * timezones other than localtime or GMT.
 * 
 * The library 'date-fns-tz' has this functionality but depends on version to of 'date-fns'
 * (this dependency isn't listed in 'date-fns-tz' but nevertheless things break without 'date-fns' version 2)
 * 
 * Problem is, that version 2 of 'date-fns' is more strict on argument types, requiring "date" arguments
 * to actually be 'Date' objects, whereas version on accepted strings.
 * 
 * Because of the joy of Javascript having no types, there's no real way I can be sure if any variable passed
 * to 'date-fns' is a date object or a string.
 * 
 * So what I've done to be safe is to replace EVERY call to "dateFns.blah" to "dateFnsCompat.blah"
 * (that is, this file) and then forward the call to the actual 'date-fns' library, but not before wrapping
 * any needed arguments with Date() constructors first. The nice thing is the Date() constructor will
 * convert strings into Date objects, whilst leaving Date objects along.
 * 
 * Ideally, we have the whole thing in typescript and we only wrap the calls that are necessary, but because
 * Javascript has no types to be safe we need to wrap EVERYTHING, until we can prove otherwise.
 * 
 * And I don't want to manually go through 200+ calls to date functions. Furthermore, for the vast majority of them
 * it's not obvious whether a conversion is required.
 * 
 * Also the version 2 of the library errors on invalid dates, whereas version 1 just silently fails. 
 * 
 * Whilst an error is nice because it's obvious to a developer, 
 * it's bad for the user because it makes the browser page go blank.
 * 
 * So for this compatability library, any functions that return a Date just return an invalid date if they get passed one,
 * and if they return a string they return "Invalid date".
 * 
 * This just hides errors, so DONT use this library for new code. You don't want bad/null dates being passed around.
 * They should either not exist or be handled explicitly where they may occur.
 * 
 * It's just a hack until we have time to fix up all the underlying issues, so we don't have users pages crashing.
 */

/* 
 * Try to replicate the default one argument "format" as much as possible from version 1.
 * Note the version 1 documentation default format string means something different in version 2 of date-fns 
 * so we need to modify it.
 */
export const format = (date, dateFormat, options) => {
  const parsedDate = new Date(date)
  return dateFns.isValid(parsedDate) ? dateFns.format(parsedDate, dateFormat != null ? dateFormat : 'yyyy-MM-dd\'T\'HH:mm:ss.SSSxxx', options) : "Invalid Date";
}

/*
 * Now we're actually passing date objects around instead of strings there's a problem, 
 * in that the 'query-string' library method 'stringify' uses the 'toString()' method to URL encode query parameters.
 * 
 * But the 'toString()' method on dates defaults to a "Thursday ..." long format.
 * 
 * Our backend doesn't like this.
 * 
 * So we hack the "toString()" method so it spits out a format our backend understands.
 * 
 * Going forward, it's probably best to avoid using 'endpointQuery()' in 'state/modules/common/utils.js', 
 * as this calls 'query-string.stringify' which has no options for changing the printing of dates.
 * 
 * Some custom method that allows for custom encoding of particular objects (like dates) is probably best going forward.
 */
const hackDateToStringMethod = date => {
  var newDate = new Date(date)
  newDate.toString = function () { 
    return format(this) 
  }
  return newDate;
}

const maybeToDate = maybeDate => {
  if (maybeDate instanceof Date) {
    return hackDateToStringMethod(maybeDate)
  } else {
    return maybeDate
  }
}

const wrapDate = f => date => {
  const actualDate = new Date(date);
  return maybeToDate(dateFns.isValid(actualDate) ? f(actualDate) : actualDate);
}
const wrapDate2 = f => (date, x) => {
  const actualDate = new Date(date);
  return maybeToDate(dateFns.isValid(actualDate) ? f(actualDate, x) : actualDate);
}

const wrapDate2Dates = f => (date1, date2) => {
  const actualDate1 = new Date(date1);
  const actualDate2 = new Date(date2);
  return maybeToDate(dateFns.isValid(actualDate1) ? dateFns.isValid(actualDate2) ? f(actualDate1, actualDate2) : actualDate2 : actualDate1);
}

export const subHours = wrapDate2(dateFns.subHours);
export const subDays = wrapDate2(dateFns.subDays);
export const subWeeks = wrapDate2(dateFns.subWeeks);

export const addMinutes = wrapDate2(dateFns.addMinutes);
export const addHours = wrapDate2(dateFns.addHours);
export const addDays = wrapDate2(dateFns.addDays);
export const addMonths = wrapDate2(dateFns.addMonths);
export const addYears = wrapDate2(dateFns.addYears);

export const differenceInCalendarDays = wrapDate2Dates(dateFns.differenceInCalendarDays);
export const differenceInCalendarWeeks = wrapDate2Dates(dateFns.differenceInCalendarWeeks);

export const setMinutes = wrapDate2(dateFns.setMinutes);
export const setHours = wrapDate2(dateFns.setHours);
export const setDate = wrapDate2(dateFns.setDate);
export const setMonth = wrapDate2(dateFns.setMonth);
export const setYear = wrapDate2(dateFns.setYear);

export const getMinutes = wrapDate(dateFns.getMinutes);
export const getHours = wrapDate(dateFns.getHours);
export const getDate = wrapDate(dateFns.getDate);
export const getDay = getDate;
export const getMonth = wrapDate(dateFns.getMonth);
export const getYear = wrapDate(dateFns.getYear);

export const parse = hackDateToStringMethod;

export const getDaysInMonth = wrapDate(dateFns.getDaysInMonth);

export const isValid = wrapDate(dateFns.isValid);

export const differenceInMilliseconds = wrapDate2Dates(dateFns.differenceInMilliseconds);

export const startOfDay = wrapDate(dateFns.startOfDay);

export const startOfToday = dateFns.startOfToday;

export const isPast = wrapDate(dateFns.isPast);

export const isFuture = wrapDate(dateFns.isFuture);

export const endOfDay = wrapDate(dateFns.endOfDay);

export const getTime = wrapDate(dateFns.getTime);

export const distanceInWordsToNow = wrapDate(dateFns.formatDistanceToNow);



// WEBPACK FOOTER //
// ./src/utils/date-fns-compat.js